// src/pages/CustomReportCreatePage.js
import React, { useEffect, useState } from 'react';
import {
  Box,
  Input,
  Select,
  Button,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Heading,
  Spinner,
  useToast,
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  GetTemplatesInfo,
  GetCustomReportById,
  CreateCustomReport,
  UpdateCustomReport,
  DownloadTemplatePreview,
} from '../api/customReport';
import { GetAllTags } from '../api/tag';
import SelectTagModal from '../components/selectTagModal.component';

const CustomReportCreatePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const isEditMode = id !== 'new';

  const [reportName, setReportName] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [time, setTime] = useState('09:00');
  const [period, setPeriod] = useState('Daily');
  const [rows, setRows] = useState([]);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [tagList, setTagList] = useState([]);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [selectedTagId, setSelectedTagId] = useState(null);
  const [isEnabled, setIsEnabled] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);

  const { data: templates } = useQuery('templateInfo', GetTemplatesInfo);

  const { data: existingReport, isLoading: isLoadingReport } = useQuery(
    ['customReport', id],
    () => GetCustomReportById(id),
    { enabled: isEditMode }
  );

  useEffect(() => {
    const fetchTagsAndSetRows = async () => {
      const tags = await GetAllTags();
      const allDevices = tags.flatMap(device => ({
        ...device,
        tags: device.tags.map(tag => ({
          ...tag,
          deviceName: device.deviceName,
          id: tag.registerId.toLowerCase(),
          name: tag.name,
        })),
      }));
      const allTagsWithDevice = allDevices.flatMap(device =>
        (device.tags || []).map(tag => ({
          ...tag,
          deviceName: device.deviceName,
        }))
      );

      let columnsSource = [];
      if (isEditMode && existingReport) {
        setReportName(existingReport.name || '');
        setSelectedTemplateId(existingReport.templateId || null);
        setTime(existingReport.time?.substring(0, 5) || '09:00');
        setPeriod(existingReport.period === 'hourly' ? 'Hourly' : 'Daily');
        setIsEnabled(existingReport.enabled);
        columnsSource = existingReport.columns;
      } else if (!isEditMode && templates && selectedTemplateId) {
        const selectedTemplate = templates?.find(
          t => t.id === selectedTemplateId
        );
        if (!selectedTemplate) {
          toast({
            title: 'Template not found',
            description: 'Please select a valid template.',
            status: 'error',
            isClosable: true,
          });
          return;
        }

        if (selectedTemplate) {
          columnsSource = selectedTemplate.columns.map(c => ({
            ...c,
            registerId: '00000000-0000-0000-0000-000000000000',
            process: '',
          }));
        }
      }

      const rows = columnsSource.map((col, index) => {
        const matchedTag = allTagsWithDevice.find(
          tag => tag.id.toLowerCase() === col.registerId?.toLowerCase()
        );

        return {
          order: index,
          title: col.isFixed ? col.title : `Column ${index + 1} Register`,
          isFixed: col.isFixed,
          requiresRegister: !col.isFixed,
          requiresProcess: !col.isFixed && !!col.process,
          registerId:
            col.registerId === '00000000-0000-0000-0000-000000000000'
              ? ''
              : col.registerId,
          registerName: col.isFixed
            ? col.title
            : matchedTag
              ? `${matchedTag.deviceName}.${matchedTag.name}`
              : '',
          process:
            col.process?.toLowerCase() === 'total'
              ? 'Total'
              : col.process?.toLowerCase() === 'average'
                ? 'Average'
                : '',
        };
      });

      setRows(rows);
    };

    fetchTagsAndSetRows();
  }, [existingReport, templates, selectedTemplateId, isEditMode]);

  const handleRegisterSelect = async index => {
    const tags = await GetAllTags();
    const formatted = [
      {
        device: tags.map(device => ({
          uuid: device.id,
          name: device.deviceName,
          connectionType: device.connectionType,
          tag: device.tags.map(tag => ({ ...tag, id: tag.registerId })),
        })),
      },
    ];
    setTagList(formatted);
    setSelectedRowIndex(index);
    setSelectedTagId(rows[index]?.registerId || null);
    setIsTagModalOpen(true);
  };

  const handleTagChosen = tagId => {
    const allDevices = tagList.flatMap(group => group.device || []);
    const allTagsWithDevice = allDevices.flatMap(device =>
      (device.tag || []).map(tag => ({ ...tag, deviceName: device.name }))
    );
    const matchedTag = allTagsWithDevice.find(tag => tag.id === tagId);
    const updatedRows = [...rows];
    updatedRows[selectedRowIndex] = {
      ...updatedRows[selectedRowIndex],
      registerId: tagId,
      registerName: matchedTag
        ? `${matchedTag.deviceName}.${matchedTag.name}`
        : tagId,
      requiresProcess: true, // 💡 yeni eklendi
      process: updatedRows[selectedRowIndex].process || '',
    };
    setRows(updatedRows);
    setIsTagModalOpen(false);
  };

  const handleProcessChange = (index, value) => {
    const updatedRows = [...rows];
    updatedRows[index].process = value;
    setRows(updatedRows);
  };

  useEffect(() => {
    if (selectedTemplateId && templates) {
      const selectedTemplate = templates.find(t => t.id === selectedTemplateId);
      if (!selectedTemplate) return;
      const updatedRows = selectedTemplate.columns.map(col => {
        const matched = existingReport?.columns.find(
          c => c.order === col.order && c.title === col.title
        );

        return {
          order: col.order,
          title: col.title,
          isFixed: col.isFixed,
          requiresRegister: col.requiresRegister,
          requiresProcess: col.requiresProcess,
          registerId: matched?.registerId || '',
          registerName: '',
          process: matched?.process || '',
        };
      });
      setRows(updatedRows);
    }
  }, [selectedTemplateId, templates, existingReport]);

  const handleSubmit = async () => {
    if (!reportName) {
      toast({
        title: 'Report name is required.',
        status: 'error',
        isClosable: true,
      });
      return;
    }
    const invalidRow = rows.find(r => r.registerId && !r.process);

    if (invalidRow) {
      toast({
        title: 'All selected tags must have process set.',
        status: 'error',
        isClosable: true,
      });
      return;
    }
    const payload = {
      name: reportName,
      templateId: selectedTemplateId,
      period,
      time: time.length === 5 ? `${time}:00` : time,
      enabled: isEnabled,
      columns: rows.map(r => ({
        order: r.order,
        title: r.title,
        registerId: r.registerId || '00000000-0000-0000-0000-000000000000',
        process: r.process || '',
      })),
    };
    try {
      if (isEditMode) {
        await UpdateCustomReport(id, payload);
        toast({
          title: 'Report updated successfully',
          status: 'success',
          isClosable: true,
        });
      } else {
        await CreateCustomReport(payload);
        toast({
          title: 'Report created successfully',
          status: 'success',
          isClosable: true,
        });
      }
      navigate('/report/custom');
    } catch (err) {
      toast({
        title: 'Error saving report',
        description: err.message,
        status: 'error',
        isClosable: true,
      });
    }
  };

  if (isEditMode && isLoadingReport) return <Spinner />;

  return (
    <Box p={5}>
      <Heading size="md" mb={4}>
        {isEditMode ? 'Edit' : 'Create'} Custom Report
      </Heading>

      <Flex direction="column" gap={4} mb={6}>
        <Input
          placeholder="Report Name"
          value={reportName}
          onChange={e => setReportName(e.target.value)}
          maxW="400px"
        />
        <Flex gap={4} alignItems="center">
          <Select
            placeholder="Select Template"
            value={selectedTemplateId || ''}
            onChange={e => setSelectedTemplateId(e.target.value)}
            maxW="300px"
            isDisabled={isEditMode}
          >
            {templates?.map(t => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
          <Button
            onClick={async () => {
              // 1. Templates henüz yüklenmediyse
              if (!templates || templates.length === 0) {
                toast({
                  title: 'Templates are still loading.',
                  status: 'warning',
                  isClosable: true,
                });
                return;
              }

              // 2. Template ID ile şablonu bul
              const selectedTemplate = templates.find(
                t => t.id === selectedTemplateId
              );

              // 3. Eğer bulamazsak
              if (!selectedTemplate) {
                toast({
                  title: 'Template not found',
                  status: 'error',
                  isClosable: true,
                });
                return;
              }

              // 4. PreviewId kontrolü
              if (!selectedTemplate.previewId) {
                toast({
                  title: 'No preview available for this template.',
                  status: 'warning',
                  isClosable: true,
                });
                return;
              }

              // 5. İndirme işlemi
              setIsDownloading(true);
              try {
                await DownloadTemplatePreview(selectedTemplate.previewId);
                toast({
                  title: 'Template downloaded.',
                  status: 'success',
                  isClosable: true,
                });
              } catch (e) {
                toast({
                  title: 'Failed to download template.',
                  status: 'error',
                  isClosable: true,
                });
              } finally {
                setIsDownloading(false);
              }
            }}
            isDisabled={!selectedTemplateId}
            isLoading={isDownloading}
          >
            Preview
          </Button>
        </Flex>
      </Flex>

      <Flex gap={4} alignItems="center" mb={6}>
        <Select
          value={period}
          onChange={e => setPeriod(e.target.value)}
          width="150px"
        >
          <option value="Daily">Daily</option>
          <option value="Hourly">Hourly</option>
        </Select>
        <Input
          type="time"
          value={time}
          onChange={e => setTime(e.target.value)}
          width="120px"
          isDisabled={period === 'Hourly'}
        />
        <Flex alignItems="center" gap={2}>
          <input
            type="checkbox"
            checked={isEnabled}
            onChange={e => setIsEnabled(e.target.checked)}
          />
          <Text>Enable</Text>
        </Flex>
      </Flex>

      <Table variant="simple" size="sm">
        <Thead>
          <Tr>
            <Th>Variable</Th>
            <Th>Value</Th>
            <Th>Variable</Th>
            <Th>Value</Th>
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((row, index) => (
            <Tr key={row.order}>
              <Td>
                {row.isFixed
                  ? `Column ${row.order + 1}`
                  : `Column ${row.order + 1} Register`}
              </Td>
              <Td>
                {row.isFixed ? (
                  <Text color="gray.500">{row.registerName}</Text>
                ) : (
                  <Text
                    cursor="pointer"
                    onClick={() => handleRegisterSelect(index)}
                    _hover={{ bg: 'gray.100' }}
                  >
                    {row.registerName ? (
                      row.registerName
                    ) : (
                      <Text color="gray.400">Select Tag</Text>
                    )}
                  </Text>
                )}
              </Td>
              <Td>{row.isFixed ? '-' : `Column ${row.order + 1} End`}</Td>
              <Td>
                {row.isFixed ? (
                  <Text color="gray.500">Fixed</Text>
                ) : row.requiresProcess ? (
                  <Select
                    placeholder="-"
                    value={row.process}
                    onChange={e => handleProcessChange(index, e.target.value)}
                    size="sm"
                  >
                    <option value="Total">Total</option>
                    <option value="Average">Average</option>
                  </Select>
                ) : (
                  <Text color="gray.500">-</Text>
                )}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      <SelectTagModal
        isOpen={isTagModalOpen}
        onClose={() => setIsTagModalOpen(false)}
        tags={tagList}
        onSelectTag={handleTagChosen}
        selectedTag={selectedTagId}
      />

      <Flex justifyContent="flex-end" gap={2} mt={6}>
        <Button colorScheme="gray" onClick={() => navigate('/report/custom')}>
          Cancel
        </Button>
        <Button colorScheme="blue" onClick={handleSubmit}>
          {isEditMode ? 'Update' : 'Save'}
        </Button>
      </Flex>
    </Box>
  );
};

export default CustomReportCreatePage;
